<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { mapState } from "vuex";
import { WALLET_MODULE } from "@/state/modules/wallet";
import { walletMethods } from "@/state/helpers";

/**
 * Who is holding company cash right now.
 *
 * Lists wallets, not users: a user who has never collected has no wallet row,
 * and a page of zero-balance rows for office staff who will never hold cash is
 * noise rather than information.
 */
export default {
  page() {
    return { title: i18n.t("module.wallet") };
  },
  components: { Layout, PageHeader },
  data() {
    return {
      search: "",
      searchTimer: null,
      withBalanceOnly: false,
      currentPage: 1,
      perPage: 15,
      fields: [
        { key: "userName", label: i18n.t("wallet.agent") },
        { key: "agentCode", label: i18n.t("label.agentCode") },
        { key: "collectedToday", label: i18n.t("wallet.collectedToday") },
        { key: "balance", label: i18n.t("wallet.balance") },
        { key: "action", label: i18n.t("label.action") },
      ],
    };
  },
  computed: {
    ...mapState(WALLET_MODULE, ["wallets"]),
    tableRows() {
      return this.wallets.data;
    },
    totalRows() {
      return this.wallets.meta?.total ?? this.wallets.data.length;
    },
    title() {
      return i18n.t("module.wallet");
    },
    breadcrumbItems() {
      return [
        { text: i18n.t("module.admin"), href: "/" },
        { text: this.title, active: true },
      ];
    },
  },
  async created() {
    await this.load();
  },
  methods: {
    ...walletMethods,
    load() {
      return this.getAllWallets({
        params: {
          page: this.currentPage,
          size: this.perPage,
          search: this.search || undefined,
          with_balance_only: this.withBalanceOnly ? 1 : undefined,
        },
      });
    },
    onSearch() {
      // Debounced: the list is a server query, not a client filter.
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1;
        this.load();
      }, 350);
    },
    onFilterToggle() {
      this.currentPage = 1;
      this.load();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.load();
    },
    money(v) {
      return Number(v ?? 0).toFixed(2);
    },
    goDetail(row) {
      this.$router.push({ name: "Wallet-detail", params: { userUuid: row.userUuid } });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbItems" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <b-row class="mb-3 align-items-end">
              <b-col cols="12" md="5">
                <b-form-group :label="$t('label.search')" class="mb-2">
                  <b-form-input v-model="search" :placeholder="$t('wallet.agent')" @input="onSearch" />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="4">
                <b-form-checkbox v-model="withBalanceOnly" class="mb-3" @change="onFilterToggle">
                  {{ $t('wallet.withBalanceOnly') }}
                </b-form-checkbox>
              </b-col>
              <b-col cols="12" md="3" class="text-md-right">
                <div class="text-muted small">{{ $t('wallet.totalHeld') }}</div>
                <h4 class="text-primary mb-0">{{ money(wallets.totalBalance) }}</h4>
              </b-col>
            </b-row>

            <b-table :items="tableRows" :fields="fields" responsive="sm" small show-empty>
              <template #empty>
                <div class="text-center text-muted py-3">{{ $t('wallet.noWallets') }}</div>
              </template>

              <template #cell(userName)="data">
                {{ tableRows[data.index].userName || '—' }}
                <div class="text-muted small">{{ tableRows[data.index].email }}</div>
              </template>

              <template #cell(agentCode)="data">
                <span class="font-monospace">{{ tableRows[data.index].agentCode || '—' }}</span>
              </template>

              <template #cell(collectedToday)="data">
                <span v-if="tableRows[data.index].collectedToday > 0" class="text-success">
                  +{{ money(tableRows[data.index].collectedToday) }}
                </span>
                <span v-else class="text-muted">—</span>
              </template>

              <template #cell(balance)="data">
                <strong :class="tableRows[data.index].balance < 0 ? 'text-danger' : 'text-primary'">
                  {{ money(tableRows[data.index].balance) }}
                </strong>
              </template>

              <template #cell(action)="data">
                <b-button size="sm" variant="outline-primary" @click="goDetail(tableRows[data.index])">
                  <i class="mdi mdi-eye-outline mr-1"></i>{{ $t('wallet.transactions') }}
                </b-button>
              </template>
            </b-table>

            <b-pagination
              v-if="totalRows > perPage"
              :value="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              align="center"
              class="mt-3 mb-0"
              @change="onPageChange"
            />
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
